import {
  FLEET_LICENSE_CLASSES,
  FleetFuelType,
  FleetOperationalStatus,
  FleetOwnership,
  FleetUsageMode,
  FleetVehicleCategory,
  type FleetVehicle,
  type FleetVehicleWrite,
  type UpdateFleetVehicleRequest,
} from '@gb-ticket/shared-types';
import { type FormEvent, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ApiRequestError } from '@/lib/api';
import {
  FLEET_CATEGORY_LABELS,
  FLEET_FUEL_LABELS,
  FLEET_OWNERSHIP_LABELS,
  FLEET_STATUS_LABELS,
  FLEET_USAGE_LABELS,
} from '@/products/fleet/features/fleet-labels';
import { fleetPanelClassName } from '@/products/fleet/features/fleet-panel';
import { useFleetDepartments } from '@/products/fleet/features/use-fleet';

type Draft = {
  licensePlate: string;
  vin: string;
  make: string;
  model: string;
  category: FleetVehicle['category'];
  fuelType: FleetVehicle['fuelType'];
  ownership: FleetVehicle['ownership'];
  usageMode: FleetVehicle['usageMode'];
  operationalStatus: 'in_service' | 'out_of_service' | 'in_workshop';
  firstRegisteredOn: string;
  requiredLicenseClass: FleetVehicle['requiredLicenseClass'];
  departmentId: string;
  leasingEndsOn: string;
  insuranceEndsOn: string;
  huFirstMonths: string;
  huNextMonths: string;
  uvvIntervalMonths: string;
  serviceIntervalKm: string;
  serviceIntervalMonths: string;
  lastServiceKm: string;
  lastServiceOn: string;
  privateUseAllowed: boolean;
  locationNote: string;
  telematicsRef: string;
  tankCapacityLiters: string;
};

function draftFrom(vehicle?: FleetVehicle): Draft {
  return {
    licensePlate: vehicle?.licensePlate ?? '',
    vin: vehicle?.vin ?? '',
    make: vehicle?.make ?? '',
    model: vehicle?.model ?? '',
    category: vehicle?.category ?? FleetVehicleCategory.CAR,
    fuelType: vehicle?.fuelType ?? FleetFuelType.PETROL,
    ownership: vehicle?.ownership ?? FleetOwnership.OWNED,
    usageMode: vehicle?.usageMode ?? FleetUsageMode.ASSIGNED,
    operationalStatus:
      vehicle?.operationalStatus === FleetOperationalStatus.RETIRED
        ? FleetOperationalStatus.IN_SERVICE
        : (vehicle?.operationalStatus ?? FleetOperationalStatus.IN_SERVICE),
    firstRegisteredOn: vehicle?.firstRegisteredOn ?? '',
    requiredLicenseClass: vehicle?.requiredLicenseClass ?? 'B',
    departmentId: vehicle?.departmentId ?? '',
    leasingEndsOn: vehicle?.leasingEndsOn ?? '',
    insuranceEndsOn: vehicle?.insuranceEndsOn ?? '',
    huFirstMonths: vehicle?.huFirstMonths?.toString() ?? '',
    huNextMonths: vehicle?.huNextMonths?.toString() ?? '',
    uvvIntervalMonths: String(vehicle?.uvvIntervalMonths ?? 12),
    serviceIntervalKm: vehicle?.serviceIntervalKm?.toString() ?? '',
    serviceIntervalMonths: vehicle?.serviceIntervalMonths?.toString() ?? '',
    lastServiceKm: vehicle?.lastServiceKm?.toString() ?? '',
    lastServiceOn: vehicle?.lastServiceOn ?? '',
    privateUseAllowed: vehicle?.privateUseAllowed ?? false,
    locationNote: vehicle?.locationNote ?? '',
    telematicsRef: vehicle?.telematicsRef ?? '',
    tankCapacityLiters: vehicle?.tankCapacityLiters?.toString() ?? '',
  };
}

function optionalNumber(value: string): number | null {
  const text = value.trim();
  if (!text) {
    return null;
  }
  return Number(text);
}

export function FleetVehicleForm({
  vehicle,
  pending,
  onCreate,
  onUpdate,
  manage = true,
  telematicsManage = false,
}: {
  vehicle?: FleetVehicle;
  pending: boolean;
  onCreate?: (body: FleetVehicleWrite) => Promise<void>;
  onUpdate?: (body: UpdateFleetVehicleRequest) => Promise<void>;
  manage?: boolean;
  telematicsManage?: boolean;
}) {
  const departments = useFleetDepartments();
  const [draft, setDraft] = useState<Draft>(() => draftFrom(vehicle));
  const [error, setError] = useState<string | null>(null);
  const retired = vehicle?.operationalStatus === FleetOperationalStatus.RETIRED;

  function patch(values: Partial<Draft>) {
    setDraft((current) => ({ ...current, ...values }));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    const shared = {
      licensePlate: draft.licensePlate,
      vin: draft.vin.trim() ? draft.vin.trim() : null,
      make: draft.make,
      model: draft.model,
      category: draft.category,
      fuelType: draft.fuelType,
      ownership: draft.ownership,
      usageMode: draft.usageMode,
      firstRegisteredOn: draft.firstRegisteredOn || null,
      requiredLicenseClass: draft.requiredLicenseClass,
      departmentId: draft.departmentId || null,
      leasingEndsOn: draft.leasingEndsOn || null,
      insuranceEndsOn: draft.insuranceEndsOn || null,
      huFirstMonths: optionalNumber(draft.huFirstMonths),
      huNextMonths: optionalNumber(draft.huNextMonths),
      uvvIntervalMonths: optionalNumber(draft.uvvIntervalMonths) ?? 12,
      serviceIntervalKm: optionalNumber(draft.serviceIntervalKm),
      serviceIntervalMonths: optionalNumber(draft.serviceIntervalMonths),
      lastServiceKm: optionalNumber(draft.lastServiceKm),
      lastServiceOn: draft.lastServiceOn || null,
      privateUseAllowed: draft.privateUseAllowed,
      locationNote: draft.locationNote.trim() ? draft.locationNote.trim() : null,
      ...(manage ? { tankCapacityLiters: optionalNumber(draft.tankCapacityLiters) } : {}),
    };
    try {
      const telematicsRef = draft.telematicsRef.trim() ? draft.telematicsRef.trim() : null;
      if (onUpdate) {
        if (!manage && telematicsManage) {
          await onUpdate({ telematicsRef });
        } else {
          await onUpdate({
            ...shared,
            operationalStatus: draft.operationalStatus,
            ...(telematicsManage ? { telematicsRef } : {}),
          });
        }
      } else if (onCreate) {
        await onCreate(shared);
      }
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Das Fahrzeug konnte nicht gespeichert werden.',
      );
    }
  }

  if (!manage && telematicsManage && onUpdate) {
    return (
      <form
        className={`${fleetPanelClassName} space-y-4`}
        onSubmit={(event) => void onSubmit(event)}
      >
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <Field
          label="Telematik-Referenz"
          value={draft.telematicsRef}
          onChange={(value) => patch({ telematicsRef: value })}
        />
        <Button type="submit" disabled={pending || retired}>
          Speichern
        </Button>
      </form>
    );
  }

  return (
    <form className={`${fleetPanelClassName} space-y-4`} onSubmit={(event) => void onSubmit(event)}>
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {retired ? (
        <p className="text-sm text-muted-foreground">Stillgelegt. Der Stamm ist nur noch lesbar.</p>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Kennzeichen"
          value={draft.licensePlate}
          onChange={(value) => patch({ licensePlate: value })}
        />
        <Field label="FIN" value={draft.vin} onChange={(value) => patch({ vin: value })} />
        <Field label="Hersteller" value={draft.make} onChange={(value) => patch({ make: value })} />
        <Field label="Modell" value={draft.model} onChange={(value) => patch({ model: value })} />
        <SelectField
          label="Art"
          value={draft.category}
          options={FleetVehicleCategory}
          labels={FLEET_CATEGORY_LABELS}
          onChange={(value) => patch({ category: value as Draft['category'] })}
        />
        <SelectField
          label="Antrieb / Kraftstoff"
          value={draft.fuelType}
          options={FleetFuelType}
          labels={FLEET_FUEL_LABELS}
          onChange={(value) => patch({ fuelType: value as Draft['fuelType'] })}
        />
        <SelectField
          label="Haltung"
          value={draft.ownership}
          options={FleetOwnership}
          labels={FLEET_OWNERSHIP_LABELS}
          onChange={(value) => patch({ ownership: value as Draft['ownership'] })}
        />
        <SelectField
          label="Nutzung"
          value={draft.usageMode}
          options={FleetUsageMode}
          labels={FLEET_USAGE_LABELS}
          onChange={(value) => patch({ usageMode: value as Draft['usageMode'] })}
        />
        <div>
          <Label htmlFor="fuehrerscheinklasse">Führerscheinklasse</Label>
          <Select
            value={draft.requiredLicenseClass}
            onValueChange={(value) =>
              patch({ requiredLicenseClass: value as Draft['requiredLicenseClass'] })
            }
          >
            <SelectTrigger id="fuehrerscheinklasse" className="mt-2 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FLEET_LICENSE_CLASSES.map((code) => (
                <SelectItem key={code} value={code}>
                  {code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="abteilung">Abteilung</Label>
          <Select
            value={draft.departmentId || NO_DEPARTMENT}
            onValueChange={(value) => patch({ departmentId: value === NO_DEPARTMENT ? '' : value })}
          >
            <SelectTrigger id="abteilung" className="mt-2 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_DEPARTMENT}>Keine</SelectItem>
              {departments.data?.items.map((department) => (
                <SelectItem key={department.id} value={department.id}>
                  {department.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Field
          label="Erstzulassung"
          type="date"
          value={draft.firstRegisteredOn}
          onChange={(value) => patch({ firstRegisteredOn: value })}
        />
        {vehicle && !retired ? (
          <SelectField
            label="Status"
            value={draft.operationalStatus}
            options={{
              IN_SERVICE: FleetOperationalStatus.IN_SERVICE,
              OUT_OF_SERVICE: FleetOperationalStatus.OUT_OF_SERVICE,
              IN_WORKSHOP: FleetOperationalStatus.IN_WORKSHOP,
            }}
            labels={FLEET_STATUS_LABELS}
            onChange={(value) => patch({ operationalStatus: value as Draft['operationalStatus'] })}
          />
        ) : null}
        <Field
          label="Leasingende"
          type="date"
          value={draft.leasingEndsOn}
          onChange={(value) => patch({ leasingEndsOn: value })}
        />
        <Field
          label="Versicherung bis"
          type="date"
          value={draft.insuranceEndsOn}
          onChange={(value) => patch({ insuranceEndsOn: value })}
        />
        <Field
          label="Erste HU (Monate, leer = Standard)"
          value={draft.huFirstMonths}
          onChange={(value) => patch({ huFirstMonths: value })}
        />
        <Field
          label="Weitere HU (Monate, leer = Standard)"
          value={draft.huNextMonths}
          onChange={(value) => patch({ huNextMonths: value })}
        />
        <Field
          label="UVV-Intervall (Monate)"
          value={draft.uvvIntervalMonths}
          onChange={(value) => patch({ uvvIntervalMonths: value })}
        />
        <Field
          label="Service alle km"
          value={draft.serviceIntervalKm}
          onChange={(value) => patch({ serviceIntervalKm: value })}
        />
        <Field
          label="Service alle Monate"
          value={draft.serviceIntervalMonths}
          onChange={(value) => patch({ serviceIntervalMonths: value })}
        />
        <Field
          label="Letzter Service-km"
          value={draft.lastServiceKm}
          onChange={(value) => patch({ lastServiceKm: value })}
        />
        <Field
          label="Letzter Service"
          type="date"
          value={draft.lastServiceOn}
          onChange={(value) => patch({ lastServiceOn: value })}
        />
        {telematicsManage ? (
          <Field
            label="Telematik-Referenz"
            value={draft.telematicsRef}
            onChange={(value) => patch({ telematicsRef: value })}
          />
        ) : null}
        {manage ? (
          <Field
            label="Tankvolumen (Liter)"
            value={draft.tankCapacityLiters}
            onChange={(value) => patch({ tankCapacityLiters: value })}
          />
        ) : null}
        <Field
          label="Standort"
          value={draft.locationNote}
          onChange={(value) => patch({ locationNote: value })}
        />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <Switch
          checked={draft.privateUseAllowed}
          onCheckedChange={(checked) => patch({ privateUseAllowed: checked })}
        />
        Privatnutzung erlaubt
      </label>
      <p className="text-sm text-muted-foreground">
        Nur ein Merkmal für den Fuhrpark. Keine Steuerberechnung.
      </p>
      {retired ? null : (
        <Button type="submit" disabled={pending}>
          Speichern
        </Button>
      )}
    </form>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
}) {
  const id = label.replace(/\s+/g, '-').toLowerCase();
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        className="mt-2"
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

const NO_DEPARTMENT = 'none';

function SelectField<T extends Record<string, string>>({
  label,
  value,
  options,
  labels,
  onChange,
}: {
  label: string;
  value: string;
  options: T;
  labels: Record<string, string>;
  onChange: (value: string) => void;
}) {
  const id = label.replace(/\s+/g, '-').toLowerCase();
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="mt-2 w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.values(options).map((option) => (
            <SelectItem key={option} value={option}>
              {labels[option] ?? option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
